import React from "react";
import { daysRemaining } from "@/lib/shelfLife";

const GENERAL_TIPS = [
  "Group like with like — fridge top shelf for ready-to-eat, drawers for greens and crisp produce.",
  "Most produce hates plastic. Reach for paper, beeswax wraps or perforated bags.",
  "Onions, potatoes and garlic want cool & dark — never the fridge. Keep onions away from potatoes.",
  "Berries last twice as long after a 1:3 vinegar rinse, dried thoroughly, in a paper-lined container.",
  "When in doubt, freeze. Bread, herbs (in oil cubes), bananas (peeled), and most cooked grains all freeze well.",
];

/**
 * Pantry tips card shown below the pantry list. Purely presentational:
 * derives everything it needs from `items`.
 */
export default function PantryTips({ items }) {
  const annotated = items
    .map((it) => ({ it, info: daysRemaining(it) }))
    .filter((x) => x.info);

  const useSoon = annotated
    .filter((x) => x.info.remaining <= 3)
    .sort((a, b) => a.info.remaining - b.info.remaining);

  const storageHints = (() => {
    const seen = new Set();
    const hints = [];
    for (const { it, info } of annotated) {
      const tip = info.info?.tips;
      if (tip && !seen.has(tip)) {
        seen.add(tip);
        hints.push({ name: it.name, tip });
      }
      if (hints.length >= 5) break;
    }
    return hints;
  })();

  return (
    <section className="mt-8 warm-card p-6" data-testid="pantry-tips">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-2xl text-hearth-ink">Pantry notes</h3>
        <span className="caption">storage tips · use-by guidance</span>
      </div>

      {useSoon.length > 0 && (
        <div className="mb-5">
          <p className="caption mb-2 text-hearth-clayDark">Use soon</p>
          <div className="flex flex-wrap gap-2">
            {useSoon.map(({ it, info }) => (
              <div key={`soon-${it.id}`} className="chip bg-hearth-coral/10 text-hearth-coral inline-flex items-center gap-1.5">
                <span className="font-medium">{it.name}</span>
                <span className="opacity-70 font-mono">
                  {info.remaining === 0 ? "today" : info.remaining === 1 ? "1d" : `${info.remaining}d`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {storageHints.length > 0 && (
        <div className="mb-5">
          <p className="caption mb-2">Storage hints for what you have</p>
          <ul className="space-y-1.5 text-sm">
            {storageHints.map((h) => (
              <li key={`hint-${h.name}-${h.tip}`} className="flex gap-2">
                <span className="w-1 h-1 rounded-full bg-hearth-clay mt-2 shrink-0" />
                <span className="text-hearth-ink"><span className="font-medium">{h.name}:</span> <span className="text-hearth-mute">{h.tip}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="caption mb-2">General pantry wisdom</p>
        <ul className="space-y-1.5 text-sm">
          {GENERAL_TIPS.map((t) => (
            <li key={t} className="flex gap-2">
              <span className="w-1 h-1 rounded-full bg-hearth-sage mt-2 shrink-0" />
              <span className="text-hearth-mute">{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
